import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

interface TelegramAuthGuardProps {
  children: ReactNode;
  fallback?: JSX.Element;
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid var(--tg-theme-hint-color, #cccccc);
  border-top: 4px solid var(--tg-theme-button-color, #0088cc);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
  background: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
`;

const ErrorTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const ErrorMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 16px;
  line-height: 1.5;
  color: var(--tg-theme-hint-color, #666666);
  max-width: 320px;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const RetryButton = styled.button`
  background: var(--tg-theme-button-color, #0088cc);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const InstructionsList = styled.ul`
  text-align: left;
  margin: 20px 0;
  padding: 0;
  list-style: none;
  color: var(--tg-theme-hint-color, #666666);
`;

const InstructionItem = styled.li`
  margin: 8px 0;
  padding-left: 20px;
  position: relative;

  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: var(--tg-theme-button-color, #0088cc);
    font-weight: bold;
  }
`;

export const TelegramAuthGuard: React.FC<TelegramAuthGuardProps> = ({
  children,
  fallback
}) => {
  const { isAuthenticated, isLoading, isTelegramWebApp, user } = useAuth();

  const handleRetry = () => {
    window.location.reload();
  };

  // Loading state
  if (isLoading) {
    return fallback ? fallback : (
      <LoadingContainer>
        <LoadingSpinner />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>
          Initializing Telegram WebApp...
        </p>
      </LoadingContainer>
    );
  }

  // Not in Telegram environment
  if (!isTelegramWebApp && process.env.NODE_ENV !== 'development') {
    return (
      <ErrorContainer>
        <ErrorIcon>📱</ErrorIcon>
        <ErrorTitle>Telegram Required</ErrorTitle>
        <ErrorMessage>
          This application can only be accessed through Telegram.
          Please open it using the bot command in Telegram.
        </ErrorMessage>
        <InstructionsList>
          <InstructionItem>Open Telegram</InstructionItem>
          <InstructionItem>Find the bot: @{process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'your_bot'}</InstructionItem>
          <InstructionItem>Send /start command</InstructionItem>
          <InstructionItem>Click "Open App" button</InstructionItem>
        </InstructionsList>
      </ErrorContainer>
    );
  }

  // Authentication failed
  if (!isAuthenticated) {
    const isDevMode = process.env.NODE_ENV === 'development';
    const telegram = (window as any).Telegram?.WebApp;
    const isDirectAccess = !isTelegramWebApp || (telegram?.platform === 'unknown');

    return (
      <ErrorContainer>
        <ErrorIcon>🔒</ErrorIcon>
        <ErrorTitle>Access Denied</ErrorTitle>
        <ErrorMessage>
          {isDirectAccess
            ? "This application can only be accessed through the official Telegram bot. Direct website access is not allowed."
            : isDevMode
              ? "Development mode: Authentication failed. Check console for details."
              : user
                ? "You don't have permission to access this application. You must be a member of an authorized group."
                : "Authentication failed. Please try again through the Telegram bot."
          }
        </ErrorMessage>

        {!isDirectAccess && (
          <InstructionsList>
            <InstructionItem>Make sure you're a member of the authorized group</InstructionItem>
            <InstructionItem>Contact the administrator if you need access</InstructionItem>
            <InstructionItem>Try refreshing the application</InstructionItem>
          </InstructionsList>
        )}

        <RetryButton onClick={handleRetry}>
          Try Again
        </RetryButton>
      </ErrorContainer>
    );
  }

  // Authentication successful - render children
  return <>{children}</>;
};